import { Button } from '@/components/Button';
import Stack from '@mui/material/Stack';
import { Filter, WifiPen } from 'lucide-react';
import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';
import { ModalAp, ModalTitle } from './styles';
import { useTranslation } from 'react-i18next';
import Card from '@/components/Card';
import {
  GlassGroupSubheader,
  GlassMenuItem,
  RoundedTextField,
  getGlassPaperSxObject,
} from '@/components/Input/SelectInput/styles';
import { useTheme } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material/Select';

// Modal types
type WirelessModalState =
  | 'radio24'
  | 'radio50'
  | 'wifi24'
  | 'wifi50'
  | 'apSelect24'
  | 'apSelect50'
  | null;
type GeneralModalState =
  | 'selectWan'
  | 'selectLan'
  | 'editWan'
  | 'editLan'
  | null;
type ManagementModalState = 'management' | 'selectWebUser' | 'webUser' | null;
type VoipModalState = 'selectVoip' | 'editVoip' | null;

type AnyModalState =
  | WirelessModalState
  | GeneralModalState
  | ManagementModalState
  | VoipModalState;

interface ApOption {
  index: number;
  name: string;
}

interface SelectApProps<T extends AnyModalState, V extends number> {
  open: boolean;
  setOpen: Dispatch<SetStateAction<T>> | ((value: T) => void);
  currentValue: V;
  setCurrentValue: Dispatch<SetStateAction<V>>;
  currentOptions?: ApOption[];
  groupedOptions?: {
    group: string;
    items: ApOption[];
  }[];
  title: string;
  label: string;
}

// Builds a display label for an AP option
function buildDisplayLabel(
  option: ApOption,
  allOptions: ApOption[],
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const hasDuplicate = allOptions.some(
    (o) => o.name === option.name && o.index !== option.index
  );
  if (!hasDuplicate) return option.name;
  return `${option.name} (${t('equipment.wifis.apNumber', { n: option.index + 1 })})`;
}

function SelectAp<T extends AnyModalState, V extends number>({
  open,
  setOpen,
  currentValue,
  setCurrentValue,
  currentOptions = [],
  groupedOptions,
  title,
  label,
}: SelectApProps<T, V>) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [selectedIndex, setSelectedIndex] = useState<number>(currentValue);

  useEffect(() => {
    if (open) {
      setSelectedIndex(currentValue);
    }
  }, [open, currentValue]);

  const handleChangeAp = () => {
    setCurrentValue(selectedIndex as V);
    setOpen(null as T);
  };

  const handleClose = () => {
    setOpen(null as T);
  };

  // Flat list used only for duplicate-detection in label building
  const allOptions: ApOption[] = groupedOptions
    ? groupedOptions.flatMap((g) => g.items)
    : currentOptions;

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const idx = Number(event.target.value);
    if (!isNaN(idx)) setSelectedIndex(idx);
  };

  const paperSx = getGlassPaperSxObject(theme);

  const renderItems = () => {
    if (groupedOptions) {
      return groupedOptions.flatMap((group) => [
        <GlassGroupSubheader key={`header-${group.group}`}>
          {group.group}
        </GlassGroupSubheader>,
        ...group.items.map((option) => (
          <GlassMenuItem key={option.index} value={String(option.index)}>
            {buildDisplayLabel(option, allOptions, t)}
          </GlassMenuItem>
        )),
      ]);
    }

    return currentOptions.map((option) => (
      <GlassMenuItem key={option.index} value={String(option.index)}>
        {buildDisplayLabel(option, allOptions, t)}
      </GlassMenuItem>
    ));
  };

  return (
    <ModalAp open={open} onClose={handleClose}>
      <Card sx={{ width: '400px', gap: '24px' }}>
        <ModalTitle variant="h4">
          <WifiPen size={20} />
          {title}
        </ModalTitle>

        <RoundedTextField
          select
          fullWidth
          label={label}
          value={String(selectedIndex)}
          onChange={handleSelectChange as never}
          InputLabelProps={{ shrink: true }}
          slotProps={{
            select: {
              startAdornment: (
                <Filter size={16} style={{ marginRight: 8, opacity: 0.6 }} />
              ),
              MenuProps: {
                PaperProps: { sx: paperSx },
              },
            },
          }}
        >
          {renderItems()}
        </RoundedTextField>

        <Stack direction="row" justifyContent="flex-end" width="100%" gap="8px">
          <Button
            variant="red"
            color="neutral"
            height="32px"
            onClick={handleClose}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            color="danger"
            height="32px"
            onClick={handleChangeAp}
          >
            {t('common.change')}
          </Button>
        </Stack>
      </Card>
    </ModalAp>
  );
}

export default SelectAp;
